import React, { useState, useEffect } from 'react';
import {
  Star,
  Trophy,
  TrendingUp,
  Search,
  Bookmark,
  Menu,
  X,
  BarChart3,
  Users,
  ArrowLeftRight,
  Key,
  Sparkles,
  CreditCard,
  FolderKanban,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';
import { StorageService } from '../services/storage';
import { AppStoreButton } from './AppStoreButton';

interface BottomTabBarProps {
  currentRoute: string;
  onNavigate: (route: string) => void;
  onOpenSearch: () => void;
  onOpenSignIn: () => void;
}

export const BottomTabBar: React.FC<BottomTabBarProps> = ({
  currentRoute,
  onNavigate,
  onOpenSearch,
  onOpenSignIn
}) => {
  const [watchlistCount, setWatchlistCount] = useState(StorageService.getWatchlist().length);
  const [user, setUser] = useState(StorageService.getUser());
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleWatchlist = () => setWatchlistCount(StorageService.getWatchlist().length);
    const handleUser = () => setUser(StorageService.getUser());

    window.addEventListener('appstatsview_watchlist_updated', handleWatchlist);
    window.addEventListener('appstatsview_user_updated', handleUser);
    return () => {
      window.removeEventListener('appstatsview_watchlist_updated', handleWatchlist);
      window.removeEventListener('appstatsview_user_updated', handleUser);
    };
  }, []);

  // Lock body scroll when mobile menu bottom sheet is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleTabClick = (tabKey: 'discover' | 'trending' | 'search' | 'watchlist' | 'menu') => {
    if (tabKey === 'discover') {
      setMenuOpen(false);
      onNavigate('/');
    } else if (tabKey === 'trending') {
      setMenuOpen(false);
      onNavigate('/trending');
    } else if (tabKey === 'search') {
      setMenuOpen(false);
      onOpenSearch();
    } else if (tabKey === 'watchlist') {
      setMenuOpen(false);
      onNavigate('/watchlist');
    } else if (tabKey === 'menu') {
      setMenuOpen(!menuOpen);
    }
  };

  const isDiscoverActive = currentRoute === '/' || currentRoute === '' || currentRoute === '/apps' || currentRoute.startsWith('/app/');
  const isTrendingActive = currentRoute === '/trending';
  const isWatchlistActive = currentRoute === '/watchlist';

  const menuSections = [
    { label: 'All Apps', route: '/apps', icon: Star, desc: 'Top rankings & catalog' },
    { label: 'Top Charts (US & Global)', route: '/charts', icon: Trophy, desc: 'Country & store leaderboards' },
    { label: 'Developers Directory', route: '/developers', icon: Users, desc: 'Publisher portfolios' },
    { label: 'Head-to-Head Compare', route: '/compare', icon: ArrowLeftRight, desc: 'Side-by-side metrics' },
    { label: 'ASO & Keyword Radar', route: '/keywords', icon: Key, desc: 'Search position tracker' },
    { label: 'AI Market Intelligence', route: '/ai-insights', icon: Sparkles, badge: 'AI', desc: 'Real-time velocity & insights' },
    { label: 'Saved Collections', route: '/collections', icon: FolderKanban, desc: 'Curated app lists' },
    { label: 'Pricing & Pro Plans', route: '/pricing', icon: CreditCard, desc: 'Upgrade analytics tier' }
  ];

  return (
    <>
      {/* Clean iOS Native Bottom Sheet when Menu is active */}
      {menuOpen && (
        <div
          id="mobile-menu-bottom-sheet-backdrop"
          className="fixed inset-0 z-50 flex flex-col justify-end bg-slate-950/40 backdrop-blur-xs animate-fadeIn md:hidden"
        >
          <div
            className="fixed inset-0 -z-10 cursor-pointer"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            id="mobile-menu-bottom-sheet"
            className="bg-white/95 backdrop-blur-2xl rounded-t-[32px] border-t border-slate-200/60 shadow-[0_-16px_48px_rgba(0,0,0,0.15)] p-5 pb-[max(env(safe-area-inset-bottom,0px),24px)] max-w-lg mx-auto w-full animate-slideUp max-h-[85vh] overflow-y-auto select-none"
          >
            {/* iOS Grab Handle */}
            <div className="w-10 h-1.5 rounded-full bg-slate-300 mx-auto mb-4 opacity-80" />

            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900 tracking-tight">Navigation & Tools</h3>
                <p className="text-xs text-slate-500">Explore AppStatsView mobile app intelligence</p>
              </div>
              <button
                id="close-mobile-menu-btn"
                type="button"
                onClick={() => setMenuOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100/90 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-all duration-150 active:scale-95"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>

            {/* Quick account banner */}
            <div className="p-3 bg-slate-50/90 rounded-2xl border border-slate-200/60 mb-4 flex items-center justify-between shadow-xs">
              {user ? (
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                  <div className="w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-xs">
                    {user.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-900 truncate">{user.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {user.email} • <span className="text-blue-600 font-semibold">{user.plan}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 min-w-0 pr-2">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                    <ShieldCheck className="w-4 h-4" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">Unlock Pro Analytics</div>
                    <div className="text-[11px] text-slate-500">Track 500+ daily store movements</div>
                  </div>
                </div>
              )}

              <button
                id="menu-profile-btn"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenSignIn();
                }}
                className="text-xs font-bold text-blue-600 bg-white border border-slate-200/80 px-3.5 py-1.5 rounded-xl hover:bg-blue-50 transition-colors shrink-0 shadow-xs active:scale-95"
              >
                {user ? 'Account' : 'Sign In'}
              </button>
            </div>

            {/* Navigation Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-semibold mb-4">
              {menuSections.map((item) => {
                const IconComponent = item.icon;
                const isActive = currentRoute === item.route;
                return (
                  <button
                    key={item.route}
                    id={`menu-item-${item.route.replace('/', '')}`}
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onNavigate(item.route);
                    }}
                    className={`p-3 rounded-2xl text-left flex items-center justify-between gap-3 transition-all duration-150 active:scale-[0.98] ${
                      isActive
                        ? 'bg-blue-50/80 text-blue-700 font-bold border border-blue-200/80'
                        : 'bg-slate-50/70 hover:bg-slate-100/80 text-slate-700 border border-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${isActive ? 'bg-blue-100/70 text-blue-600' : 'bg-white text-slate-500 shadow-2xs'}`}>
                        <IconComponent className="w-4 h-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900">{item.label}</div>
                        <div className="text-[10px] text-slate-400 font-normal truncate">{item.desc}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {item.badge && (
                        <span className="text-[9px] bg-indigo-100 text-indigo-700 font-bold px-1.5 py-0.5 rounded-md">
                          {item.badge}
                        </span>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
              <AppStoreButton variant="mini" />
              <span className="text-[11px] text-slate-400 font-medium">v2.4.0 • Real-Time Engine</span>
            </div>
          </div>
        </div>
      )}

      {/* Floating Apple Native-Style Bottom Dock (Mobile Only) */}
      <aside
        id="mobile-bottom-dock"
        aria-label="Mobile Navigation Dock"
        className="fixed bottom-[max(env(safe-area-inset-bottom,0px),12px)] inset-x-3.5 max-w-[420px] mx-auto z-40 md:hidden select-none pointer-events-auto"
      >
        <div className="bg-white/85 backdrop-blur-2xl backdrop-saturate-150 rounded-[28px] border border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)] px-2 py-1.5">
          <div className="grid grid-cols-5 h-14 items-center">
            {/* 1. Discover */}
            <button
              id="tab-discover"
              type="button"
              onClick={() => handleTabClick('discover')}
              className={`flex flex-col items-center justify-center h-full w-full rounded-2xl transition-all duration-150 active:scale-90 ${
                isDiscoverActive && !menuOpen
                  ? 'text-blue-600 font-semibold'
                  : 'text-slate-400 font-medium hover:text-slate-600'
              }`}
            >
              <Star
                className={`w-5 h-5 transition-transform duration-150 ${
                  isDiscoverActive && !menuOpen ? 'scale-105 fill-blue-600/15 text-blue-600' : 'text-slate-400'
                }`}
                strokeWidth={isDiscoverActive && !menuOpen ? 2.25 : 1.75}
              />
              <span className="text-[10px] tracking-tight leading-none mt-1">Discover</span>
            </button>

            {/* 2. Trending */}
            <button
              id="tab-trending"
              type="button"
              onClick={() => handleTabClick('trending')}
              className={`flex flex-col items-center justify-center h-full w-full rounded-2xl transition-all duration-150 active:scale-90 ${
                isTrendingActive && !menuOpen
                  ? 'text-blue-600 font-semibold'
                  : 'text-slate-400 font-medium hover:text-slate-600'
              }`}
            >
              <TrendingUp
                className={`w-5 h-5 transition-transform duration-150 ${
                  isTrendingActive && !menuOpen ? 'scale-105 text-blue-600' : 'text-slate-400'
                }`}
                strokeWidth={isTrendingActive && !menuOpen ? 2.25 : 1.75}
              />
              <span className="text-[10px] tracking-tight leading-none mt-1">Trending</span>
            </button>

            {/* 3. Search */}
            <button
              id="tab-search"
              type="button"
              onClick={() => handleTabClick('search')}
              className="flex flex-col items-center justify-center h-full w-full rounded-2xl text-slate-400 font-medium hover:text-slate-600 transition-all duration-150 active:scale-90"
            >
              <Search className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
              <span className="text-[10px] tracking-tight leading-none mt-1">Search</span>
            </button>

            {/* 4. Watchlist */}
            <button
              id="tab-watchlist"
              type="button"
              onClick={() => handleTabClick('watchlist')}
              className={`relative flex flex-col items-center justify-center h-full w-full rounded-2xl transition-all duration-150 active:scale-90 ${
                isWatchlistActive && !menuOpen
                  ? 'text-blue-600 font-semibold'
                  : 'text-slate-400 font-medium hover:text-slate-600'
              }`}
            >
              <div className="relative">
                <Bookmark
                  className={`w-5 h-5 transition-transform duration-150 ${
                    isWatchlistActive && !menuOpen ? 'scale-105 fill-blue-600/15 text-blue-600' : 'text-slate-400'
                  }`}
                  strokeWidth={isWatchlistActive && !menuOpen ? 2.25 : 1.75}
                />
                {watchlistCount > 0 && (
                  <span className="absolute -top-1 -right-2.5 min-w-[17px] h-[17px] px-1 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-2xs leading-none">
                    {watchlistCount > 99 ? '99+' : watchlistCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] tracking-tight leading-none mt-1">Watchlist</span>
            </button>

            {/* 5. Menu */}
            <button
              id="tab-menu"
              type="button"
              onClick={() => handleTabClick('menu')}
              className={`flex flex-col items-center justify-center h-full w-full rounded-2xl transition-all duration-150 active:scale-90 ${
                menuOpen
                  ? 'text-blue-600 font-semibold'
                  : 'text-slate-400 font-medium hover:text-slate-600'
              }`}
            >
              <Menu
                className={`w-5 h-5 transition-transform duration-150 ${
                  menuOpen ? 'scale-105 text-blue-600' : 'text-slate-400'
                }`}
                strokeWidth={menuOpen ? 2.25 : 1.75}
              />
              <span className="text-[10px] tracking-tight leading-none mt-1">Menu</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};
